import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ConfirmDelete } from "@/components/admin/shared";
import { ProjectDialog } from "@/components/admin/ProjectDialog";
import { ProgressBar, StatusBadge, UnreadBadge, fmtDue } from "./ProjectBits";
import { FilesPanel } from "./FilesPanel";
import { MessagesPanel } from "./MessagesPanel";
import { LinkedStudioSites } from "@/components/studio/LinkedStudioSites";

const AdminActions = ({ onEdit, onDelete }) => (
  <div className="flex gap-2">
    <button onClick={onEdit} data-testid="project-edit-button" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-sm text-slate-200 hover:border-cyan-400/50"><Pencil className="h-3.5 w-3.5" /> Edit</button>
    <button onClick={onDelete} data-testid="project-delete-button" className="inline-flex items-center gap-1.5 rounded-full border border-red-400/30 px-4 py-2 text-sm text-red-300 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
  </div>
);

export default function ProjectDetail({ admin = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [unread, setUnread] = useState(0);
  const back = admin ? "/admin/projects" : "/portal";

  const load = () => api.get(`/projects/${id}`).then((r) => { setProject(r.data); setUnread(r.data.unread_count); })
    .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (payload) => {
    const { data } = await api.put(`/admin/projects/${id}`, payload);
    setProject(data);
    setEditing(false);
    toast.success("Project updated");
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/projects/${id}`);
      toast.success("Project deleted");
      navigate(back, { replace: true });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (error) return <div data-testid="project-error" className="rounded-2xl border border-red-400/20 bg-red-500/5 p-8 text-center text-red-300">{error} <Link to={back} className="ml-2 text-cyan-300">Go back</Link></div>;
  if (!project) return <div className="h-48 animate-pulse rounded-2xl bg-white/[0.03]" />;

  return (
    <div data-testid="project-detail-page">
      <Link to={back} data-testid="project-back-link" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-300"><ArrowLeft className="h-4 w-4" /> All projects</Link>
      <div className="card-tech mb-8 rounded-2xl p-6 sm:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            {admin && <p className="font-mono-tech text-xs text-cyan-400">{project.client_name}{project.client_company ? ` · ${project.client_company}` : ""}</p>}
            <h1 data-testid="project-detail-name" className="mt-1 break-words text-2xl font-bold tracking-tight text-white sm:text-3xl">{project.name}</h1>
          </div>
          {admin && <AdminActions onEdit={() => setEditing(true)} onDelete={() => setDeleting(true)} />}
        </div>
        {project.description && <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-400">{project.description}</p>}
        <div className="mt-6 grid gap-5 sm:grid-cols-[auto_1fr_auto] sm:items-center">
          <StatusBadge status={project.status} testId="project-detail-status" />
          <ProgressBar value={project.progress} testId="project-detail-progress" />
          <span data-testid="project-detail-due" className="inline-flex items-center gap-1.5 text-sm text-slate-400"><CalendarDays className="h-4 w-4" /> {fmtDue(project.due_date)}</span>
        </div>
      </div>
      {admin && <LinkedStudioSites projectId={id} />}
      <Tabs defaultValue="files" onValueChange={(v) => v === "messages" && setUnread(0)}>
        <TabsList className="mb-6 bg-[#0B0F18]">
          <TabsTrigger value="files" data-testid="project-tab-files">Files</TabsTrigger>
          <TabsTrigger value="messages" data-testid="project-tab-messages" className="gap-2">Messages <UnreadBadge count={unread} testId="project-messages-unread-badge" /></TabsTrigger>
        </TabsList>
        <TabsContent value="files"><FilesPanel projectId={id} /></TabsContent>
        <TabsContent value="messages"><MessagesPanel projectId={id} /></TabsContent>
      </Tabs>
      {admin && <ProjectDialog open={editing} item={project} onClose={() => setEditing(false)} onSave={save} />}
      <ConfirmDelete target={deleting ? project : null} label={`"${project.name}" and all its files and messages`} onCancel={() => setDeleting(false)} onConfirm={remove} />
    </div>
  );
}
